import { useEffect, useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { 
  MoreVertical,
  Star,
  Archive,
  Trash,
  FileText,
  PenTool,
  Loader2
} from 'lucide-react'
import { apiClient } from '@/lib/api-client'
import { useWorkspaceStore } from '@/stores/workspace-store'
import type { Document, UpdateDocumentDto } from '@/types/workspace'
import { useToast } from '@/hooks/use-toast'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SimpleBlockSuiteEditor, type DocMode } from '@/components/blocksuite/simple-blocksuite-editor'

interface DocumentEditorProps {
  documentId: string
}

export function DocumentEditor({ documentId }: DocumentEditorProps) {
  const { toast } = useToast()
  const [document, setDocument] = useState<Document | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [mode, setMode] = useState<DocMode>('page')
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    loadDocument()
  }, [documentId])

  const loadDocument = async () => {
    setLoading(true)
    
    try {
      const doc = await apiClient.getDocument(documentId)
      setDocument(doc)
      
      // Check if document has edgeless data
      if (doc.blockData && doc.blockData.mode === 'edgeless') {
        setMode('edgeless')
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'Failed to load document',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const saveDocument = async (snapshot: any) => {
    if (!document) return
    
    // Debounce saves
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }
    
    saveTimeoutRef.current = setTimeout(async () => {
      setSaving(true)
      
      try {
        const updates: UpdateDocumentDto = {
          yjsData: JSON.stringify(snapshot),
          blockData: { ...snapshot, mode }
        }
        
        await apiClient.updateDocument(document.id, updates)
      } catch (error: any) {
        toast({
          title: 'Save failed',
          description: error.message || 'Failed to save document',
          variant: 'destructive',
        })
      } finally {
        setSaving(false)
      }
    }, 1000) // Save after 1 second of inactivity
  }

  const toggleFavorite = async () => {
    if (!document) return
    try {
      const updated = await apiClient.updateDocument(document.id, { 
        isFavorite: !document.isFavorite 
      })
      setDocument(updated)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update favorite status',
        variant: 'destructive',
      })
    }
  }

  const toggleMode = () => {
    const newMode = mode === 'page' ? 'edgeless' : 'page'
    setMode(newMode)
    
    // Save mode preference
    if (document) {
      apiClient.updateDocument(document.id, {
        blockData: { ...(document.blockData || {}), mode: newMode }
      })
    }
  }

  const deleteDocument = async () => {
    if (!document || !confirm('Are you sure you want to delete this document?')) return
    
    try {
      await apiClient.deleteDocument(document.id)
      toast({
        title: 'Document deleted',
        description: 'The document has been permanently deleted',
      })
      
      // Close the tab
      const { closeTab, tabs } = useWorkspaceStore.getState()
      const currentTab = tabs.find(tab => tab.documentId === documentId)
      if (currentTab) {
        closeTab(currentTab.id)
      }
    } catch (error: any) {
      toast({
        title: 'Delete failed',
        description: error.message || 'Failed to delete document',
        variant: 'destructive',
      })
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!document) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <FileText className="h-12 w-12 text-muted-foreground mb-4 mx-auto" />
          <h3 className="text-lg font-medium mb-2">Document not found</h3>
          <p className="text-muted-foreground">The document could not be loaded</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b">
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold">{document.title}</h1>
          {document.isFavorite && (
            <Star className="h-4 w-4 text-yellow-500 fill-current" />
          )}
          {saving && (
            <Badge variant="secondary" className="text-xs">
              Saving...
            </Badge>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleMode}
            title={mode === 'page' ? 'Switch to Edgeless' : 'Switch to Page'}
          >
            {mode === 'page' ? (
              <PenTool className="h-4 w-4" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleFavorite}
          >
            <Star className={`h-4 w-4 ${document.isFavorite ? 'text-yellow-500 fill-current' : ''}`} />
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>
                <Archive className="h-4 w-4 mr-2" />
                Archive
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={deleteDocument}
                className="text-red-600 focus:text-red-600"
              >
                <Trash className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* Editor */}
      <div className="flex-1 overflow-hidden">
        <SimpleBlockSuiteEditor
          documentId={document.id}
          mode={mode}
          onContentChange={saveDocument}
        />
      </div>
    </div>
  )
}